import React, { useRef, useEffect, useState } from "react";

// ---------- Pixel sprites (Galaga-style) ----------
const PALETTE = {
  W: "#f5f7ff", R: "#ff3b4e", B: "#3b7bff", Y: "#ffd23b",
  C: "#39e6ff", M: "#ff4fd8", G: "#54ff7a", P: "#a06bff", O: "#ff8c2e",
};

const SPRITES = {
  player: [
    "......W......",
    "......W......",
    ".....WWW.....",
    ".....WBW.....",
    "..R..WBW..R..",
    "..R.WWBWW.R..",
    "..RWWWBWWWR..",
    ".RRWWWBWWWRR.",
    ".RWWRWBWRWWR.",
    ".WW.RWWWR.WW.",
    "......W......",
  ],
  bee: [
    "..Y.....Y..",
    "...Y...Y...",
    "..BYYYYYB..",
    ".BBYYYYYBB.",
    "BB.YYYYY.BB",
    "...YY.YY...",
    "..YY...YY..",
  ],
  butterfly: [
    "M.........M",
    ".M.......M.",
    ".MWWM.MWWM.",
    "..MWWMWWM..",
    "...WWWWW...",
    "..W.WWW.W..",
    ".W...W...W.",
  ],
  boss: [
    "..G.....G..",
    ".GG.....GG.",
    ".GPPGGGPPG.",
    "GGPPGGGPPGG",
    "GG.GGGGG.GG",
    "..GG.G.GG..",
    ".G...G...G.",
  ],
};

function cubic(p0, p1, p2, p3, t) {
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

const W = 400, H = 700; // logical game size

export default function StarSwarm() {
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const [phase, setPhase] = useState("title"); // title | playing | gameover
  const [uiScore, setUiScore] = useState(0);
  const [uiHi, setUiHi] = useState(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // ---------- Game creation ----------
  function newGame() {
    return {
      t: 0, score: 0, hi: gameRef.current ? Math.max(gameRef.current.hi, gameRef.current.score) : 0,
      lives: 3, wave: 0, waveTimer: 0, waveState: "announce",
      player: { x: W / 2, y: H - 70, targetX: W / 2, alive: true, respawn: 0, inv: 0 },
      bullets: [], enemyBullets: [], enemies: [], particles: [], stars: makeStars(),
      fireCd: 0, diveCd: 180, shake: 0,
    };
  }

  function makeStars() {
    const s = [];
    for (let i = 0; i < 70; i++)
      s.push({ x: Math.random() * W, y: Math.random() * H, sp: 0.4 + Math.random() * 1.8, r: Math.random() < 0.15 ? 1.6 : 1 });
    return s;
  }

  // ---------- Wave / formation ----------
  function spawnWave(g) {
    g.wave++;
    g.enemies = [];
    const rows = [
      { type: "boss", count: 4, y: 90, pts: 150 },
      { type: "butterfly", count: 8, y: 130, pts: 80 },
      { type: "butterfly", count: 8, y: 165, pts: 80 },
      { type: "bee", count: 10, y: 200, pts: 50 },
      { type: "bee", count: 10, y: 235, pts: 50 },
    ];
    let id = 0;
    rows.forEach((row, ri) => {
      const gap = Math.min(34, (W - 60) / row.count);
      const startX = W / 2 - ((row.count - 1) * gap) / 2;
      for (let i = 0; i < row.count; i++) {
        const fromLeft = (ri + i) % 2 === 0;
        const sx = fromLeft ? -30 : W + 30;
        const sy = -30 - Math.random() * 40;
        const slot = { x: startX + i * gap, y: row.y };
        // choreographed swoop: off-screen -> loop through mid field -> slot
        const p1 = { x: fromLeft ? W * 0.15 : W * 0.85, y: H * 0.55 };
        const p2 = { x: fromLeft ? W * 0.85 : W * 0.15, y: H * 0.35 };
        g.enemies.push({
          id: id++, type: row.type, pts: row.pts, slot,
          x: sx, y: sy, state: "entering",
          path: [{ x: sx, y: sy }, p1, p2, { x: slot.x, y: slot.y }],
          pt: 0, delay: id * 6 + ri * 10, wob: Math.random() * Math.PI * 2,
          divePath: null, dt: 0, hp: row.type === "boss" ? 2 : 1,
        });
      }
    });
  }

  // ---------- Diving attacks ----------
  function startDive(g, e) {
    const px = g.player.x;
    e.state = "diving";
    e.dt = 0;
    e.divePath = [
      { x: e.x, y: e.y },
      { x: e.x + (Math.random() < 0.5 ? -120 : 120), y: e.y + 120 },
      { x: px + (Math.random() * 160 - 80), y: H * 0.65 },
      { x: px, y: H + 60 },
    ];
  }

  function explode(g, x, y, color, n = 14) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, sp = 1 + Math.random() * 3;
      g.particles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 26 + Math.random() * 14, color });
    }
  }

  // ---------- Update ----------
  function update(g) {
    g.t++;
    g.stars.forEach(s => { s.y += s.sp; if (s.y > H) { s.y = -2; s.x = Math.random() * W; } });
    if (g.shake > 0) g.shake--;

    // wave flow
    if (g.waveState === "announce") {
      g.waveTimer++;
      if (g.waveTimer === 1) spawnWave(g);
      if (g.waveTimer > 90) g.waveState = "battle";
    } else if (g.enemies.length === 0) {
      g.waveState = "announce"; g.waveTimer = 0;
    }

    // player
    const p = g.player;
    if (!p.alive) {
      p.respawn--;
      if (p.respawn <= 0 && g.lives > 0) { p.alive = true; p.inv = 120; p.x = p.targetX = W / 2; }
    } else {
      p.x += (p.targetX - p.x) * 0.25;
      if (p.inv > 0) p.inv--;
      // auto-fire
      g.fireCd--;
      if (g.fireCd <= 0) {
        g.bullets.push({ x: p.x, y: p.y - 16 });
        g.fireCd = 16;
      }
    }

    // bullets
    g.bullets.forEach(b => (b.y -= 9));
    g.bullets = g.bullets.filter(b => b.y > -10);
    g.enemyBullets.forEach(b => { b.x += b.vx; b.y += b.vy; });
    g.enemyBullets = g.enemyBullets.filter(b => b.y < H + 10 && b.x > -10 && b.x < W + 10);

    // formation sway + breathing
    const sway = Math.sin(g.t * 0.02) * 22;
    const breathe = 1 + Math.sin(g.t * 0.04) * 0.04;

    // enemies
    const diveSpeed = 0.006 + g.wave * 0.0007;
    g.enemies.forEach(e => {
      e.wob += 0.15;
      if (e.state === "entering") {
        if (e.delay > 0) { e.delay--; return; }
        e.pt += 0.008 + g.wave * 0.0005;
        const q = cubic(e.path[0], e.path[1], e.path[2], e.path[3], Math.min(e.pt, 1));
        e.x = q.x; e.y = q.y;
        if (e.pt >= 1) e.state = "formation";
      } else if (e.state === "formation") {
        const fx = W / 2 + (e.slot.x - W / 2) * breathe + sway;
        e.x += (fx - e.x) * 0.1;
        e.y += (e.slot.y - e.y) * 0.1;
      } else if (e.state === "diving") {
        e.dt += diveSpeed;
        if (e.dt >= 1) {
          // loop back to top, re-enter formation
          e.state = "entering"; e.pt = 0; e.delay = 0;
          e.path = [{ x: e.x, y: -30 }, { x: W - e.slot.x, y: 80 }, { x: e.slot.x, y: e.slot.y - 60 }, { x: e.slot.x, y: e.slot.y }];
          e.y = -30;
        } else {
          const q = cubic(e.divePath[0], e.divePath[1], e.divePath[2], e.divePath[3], e.dt);
          e.x = q.x; e.y = q.y;
          // fire at player mid-dive
          if (Math.random() < 0.02 && e.y < p.y - 80) {
            const dx = p.x - e.x, dy = p.y - e.y, m = Math.hypot(dx, dy);
            g.enemyBullets.push({ x: e.x, y: e.y, vx: (dx / m) * 3.2, vy: (dy / m) * 3.2 });
          }
        }
      }
    });

    // trigger dives
    g.diveCd--;
    if (g.diveCd <= 0 && g.waveState === "battle") {
      const ready = g.enemies.filter(e => e.state === "formation");
      if (ready.length) {
        const n = Math.min(1 + Math.floor(g.wave / 2), 3);
        for (let i = 0; i < n && ready.length; i++) {
          const e = ready.splice(Math.floor(Math.random() * ready.length), 1)[0];
          startDive(g, e);
        }
      }
      g.diveCd = Math.max(60, 200 - g.wave * 12);
    }

    // collisions: player bullets vs enemies
    g.bullets = g.bullets.filter(b => {
      for (const e of g.enemies) {
        if (Math.abs(b.x - e.x) < 14 && Math.abs(b.y - e.y) < 12) {
          e.hp--;
          if (e.hp <= 0) {
            e.dead = true;
            g.score += e.pts * (e.state === "diving" ? 2 : 1);
            explode(g, e.x, e.y, e.type === "bee" ? PALETTE.Y : e.type === "boss" ? PALETTE.G : PALETTE.M);
          } else {
            explode(g, e.x, e.y, PALETTE.P, 5);
          }
          return false;
        }
      }
      return true;
    });
    g.enemies = g.enemies.filter(e => !e.dead);

    // enemy bullets / bodies vs player
    if (p.alive && p.inv <= 0) {
      let hit = false;
      g.enemyBullets = g.enemyBullets.filter(b => {
        if (Math.abs(b.x - p.x) < 12 && Math.abs(b.y - p.y) < 12) { hit = true; return false; }
        return true;
      });
      for (const e of g.enemies) {
        if (e.state === "diving" && Math.abs(e.x - p.x) < 16 && Math.abs(e.y - p.y) < 14) {
          hit = true; e.dead = true;
          explode(g, e.x, e.y, PALETTE.M);
        }
      }
      g.enemies = g.enemies.filter(e => !e.dead);
      if (hit) {
        explode(g, p.x, p.y, PALETTE.C, 26);
        explode(g, p.x, p.y, PALETTE.R, 16);
        g.shake = 18;
        p.alive = false; p.respawn = 90;
        g.lives--;
        if (g.lives <= 0) {
          g.hi = Math.max(g.hi, g.score);
          setUiScore(g.score); setUiHi(g.hi);
          setPhase("gameover");
        }
      }
    }

    // particles
    g.particles.forEach(pt => { pt.x += pt.vx; pt.y += pt.vy; pt.vx *= 0.96; pt.vy *= 0.96; pt.life--; });
    g.particles = g.particles.filter(pt => pt.life > 0);
  }

  // ---------- Draw ----------
  function drawSprite(ctx, key, x, y, scale, flap) {
    const rows = SPRITES[key];
    const pw = rows[0].length, ph = rows.length;
    ctx.save();
    ctx.translate(x, y);
    if (flap) ctx.scale(1 + Math.sin(flap) * 0.08, 1 - Math.sin(flap) * 0.08);
    ctx.translate(-(pw * scale) / 2, -(ph * scale) / 2);
    for (let r = 0; r < ph; r++)
      for (let c = 0; c < pw; c++) {
        const ch = rows[r][c];
        if (ch !== ".") { ctx.fillStyle = PALETTE[ch]; ctx.fillRect(c * scale, r * scale, scale, scale); }
      }
    ctx.restore();
  }

  function draw(ctx, g) {
    ctx.fillStyle = "#04040f";
    ctx.fillRect(0, 0, W, H);

    ctx.save();
    if (g.shake > 0) ctx.translate((Math.random() - 0.5) * g.shake * 0.6, (Math.random() - 0.5) * g.shake * 0.6);

    // stars
    g.stars.forEach(s => {
      ctx.fillStyle = s.r > 1 ? "#9db4ff" : "#4a5a8a";
      ctx.fillRect(s.x, s.y, s.r, s.r);
    });

    // particles
    g.particles.forEach(pt => {
      ctx.globalAlpha = Math.max(pt.life / 30, 0);
      ctx.fillStyle = pt.color;
      ctx.fillRect(pt.x - 2, pt.y - 2, 4, 4);
    });
    ctx.globalAlpha = 1;

    // enemies
    g.enemies.forEach(e => {
      drawSprite(ctx, e.type, e.x, e.y, 2.4, e.wob);
    });

    // bullets
    ctx.fillStyle = PALETTE.C;
    g.bullets.forEach(b => ctx.fillRect(b.x - 1.5, b.y - 7, 3, 12));
    ctx.fillStyle = PALETTE.O;
    g.enemyBullets.forEach(b => ctx.fillRect(b.x - 2, b.y - 2, 4, 8));

    // player
    const p = g.player;
    if (p.alive && (p.inv <= 0 || Math.floor(g.t / 4) % 2 === 0)) {
      drawSprite(ctx, "player", p.x, p.y, 2.4);
    }

    ctx.restore();

    // HUD
    ctx.fillStyle = "#8fa3d9";
    ctx.font = "10px monospace";
    ctx.textAlign = "left";
    ctx.fillText("SCORE", 14, 20);
    ctx.textAlign = "right";
    ctx.fillText("HI-SCORE", W - 14, 20);
    ctx.font = "bold 16px monospace";
    ctx.fillStyle = "#f5f7ff";
    ctx.textAlign = "left";
    ctx.fillText(String(g.score).padStart(6, "0"), 14, 38);
    ctx.textAlign = "right";
    ctx.fillText(String(Math.max(g.hi, g.score)).padStart(6, "0"), W - 14, 38);

    // lives
    for (let i = 0; i < g.lives - (g.player.alive ? 1 : 0); i++)
      drawSprite(ctx, "player", 24 + i * 26, H - 20, 1.3);

    // wave announce
    if (g.waveState === "announce" && g.waveTimer > 10) {
      ctx.textAlign = "center";
      ctx.fillStyle = PALETTE.C;
      ctx.font = "bold 22px monospace";
      const blink = Math.floor(g.t / 12) % 2 === 0;
      if (blink) ctx.fillText(`WAVE ${g.wave}`, W / 2, H / 2 - 40);
    }
  }

  // ---------- Loop + input ----------
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!gameRef.current) gameRef.current = newGame();

    let raf;
    const loop = () => {
      const g = gameRef.current;
      if (phaseRef.current === "playing") update(g);
      draw(ctx, g);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pointer / touch: drag anywhere to steer
  useEffect(() => {
    const canvas = canvasRef.current;
    const toGameX = clientX => {
      const rect = canvas.getBoundingClientRect();
      return ((clientX - rect.left) / rect.width) * W;
    };
    let dragging = false;
    const down = e => { dragging = true; move(e); };
    const move = e => {
      if (!dragging) return;
      const x = toGameX(e.touches ? e.touches[0].clientX : e.clientX);
      const g = gameRef.current;
      if (g) g.player.targetX = Math.max(20, Math.min(W - 20, x));
      if (e.cancelable) e.preventDefault();
    };
    const up = () => { dragging = false; };
    canvas.addEventListener("touchstart", down, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", up);
    canvas.addEventListener("mousedown", down);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      canvas.removeEventListener("touchstart", down);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", up);
      canvas.removeEventListener("mousedown", down);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, []);

  // keyboard support (desktop testing)
  useEffect(() => {
    const keys = {};
    const kd = e => { keys[e.key] = true; };
    const ku = e => { keys[e.key] = false; };
    const iv = setInterval(() => {
      const g = gameRef.current;
      if (!g || phaseRef.current !== "playing") return;
      if (keys.ArrowLeft || keys.a) g.player.targetX = Math.max(20, g.player.targetX - 8);
      if (keys.ArrowRight || keys.d) g.player.targetX = Math.min(W - 20, g.player.targetX + 8);
    }, 16);
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => { clearInterval(iv); window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, []);

  const start = () => {
    const hi = gameRef.current ? Math.max(gameRef.current.hi, gameRef.current.score) : 0;
    gameRef.current = newGame();
    gameRef.current.hi = hi;
    setPhase("playing");
  };

  // ---------- UI ----------
  return (
    <div style={{
      width: "100%", height: "100vh", background: "#04040f",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "monospace", overflow: "hidden", touchAction: "none", position: "relative",
    }}>
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{
          height: "100%", maxWidth: "100%", aspectRatio: `${W}/${H}`,
          objectFit: "contain", display: "block", cursor: "crosshair",
          imageRendering: "pixelated",
        }}
      />

      {phase !== "playing" && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 18,
          background: "rgba(4,4,15,0.82)", color: "#f5f7ff", textAlign: "center", padding: 24,
        }}>
          {phase === "title" ? (
            <>
              <div style={{ fontSize: 40, fontWeight: 700, letterSpacing: 6, color: "#39e6ff", textShadow: "0 0 24px rgba(57,230,255,0.6)" }}>
                STAR SWARM
              </div>
              <div style={{ color: "#8fa3d9", fontSize: 13, maxWidth: 280, lineHeight: 1.7 }}>
                Drag to steer. Your ship fires on its own.
                Diving enemies are worth double points.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: 4, color: "#ff3b4e" }}>GAME OVER</div>
              <div style={{ color: "#8fa3d9", fontSize: 14 }}>
                Score {String(uiScore).padStart(6, "0")} · Best {String(uiHi).padStart(6, "0")}
              </div>
            </>
          )}
          <button
            onClick={start}
            style={{
              marginTop: 8, padding: "14px 40px", fontSize: 16, fontFamily: "monospace",
              fontWeight: 700, letterSpacing: 3, color: "#04040f", background: "#39e6ff",
              border: "none", borderRadius: 4, cursor: "pointer",
              boxShadow: "0 0 20px rgba(57,230,255,0.5)",
            }}
          >
            {phase === "title" ? "PLAY" : "PLAY AGAIN"}
          </button>
        </div>
      )}
    </div>
  );
}
